import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  ArrowBackIosNew,
  ArrowForwardIos,
  CalendarMonth,
  Refresh,
  Timeline,
} from '@mui/icons-material';
import { NavigateLinks } from '@/components';
import { liveTrackingChildrenPageBreadcrumbs } from '../../config';
import { TrackingIconSlot } from '../models/TrackingIcon';
import { useGetTrackingIconAvailabilityQuery } from '../services/tracking-icons.service';
import {
  buildTrackingIconSchedule,
  monthBounds,
} from '../utils/trackingIconSchedule';
import TrackingIconScheduleView from '../components/TrackingIconScheduleView';

const monthValue = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const parseMonth = (value: string | null) => {
  if (!value || !/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) return null;
  const [year, month] = value.split('-').map(Number);
  return year >= 1900 && year <= 9999 ? new Date(year, month - 1, 1) : null;
};
const monthTitle = new Intl.DateTimeFormat('es-UY', {
  month: 'long',
  year: 'numeric',
});

const TrackingIconSchedulePage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const breadcrumbs = useMemo(
    () => liveTrackingChildrenPageBreadcrumbs(navigate),
    [navigate]
  );
  const now = new Date();
  const selectedMonth =
    parseMonth(params.get('month')) ??
    new Date(now.getFullYear(), now.getMonth(), 1);
  const monthKey = monthValue(selectedMonth);
  const month = useMemo(() => parseMonth(monthKey)!, [monthKey]);
  const slotFilter =
    params.get('slot') === 'driver'
      ? 'driver'
      : params.get('slot') === 'destination'
        ? 'destination'
        : 'all';
  const slots = useMemo<TrackingIconSlot[]>(
    () => (slotFilter === 'all' ? ['driver', 'destination'] : [slotFilter]),
    [slotFilter]
  );
  const view = params.get('view') === 'calendar' ? 'calendar' : 'timeline';
  const lastDay = new Date(
    month.getFullYear(),
    month.getMonth() + 1,
    0
  ).getDate();
  const dayParam = Number(params.get('day'));
  const day =
    Number.isInteger(dayParam) && dayParam >= 1 && dayParam <= lastDay
      ? dayParam
      : monthKey === monthValue(now)
        ? now.getDate()
        : 1;
  const selectedDay = new Date(month.getFullYear(), month.getMonth(), day);
  const driver = useGetTrackingIconAvailabilityQuery('driver', {
    skip: slotFilter === 'destination',
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const destination = useGetTrackingIconAvailabilityQuery('destination', {
    skip: slotFilter === 'driver',
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const queries = slots.map((slot) =>
    slot === 'driver' ? driver : destination
  );
  const failed = queries.some((query) => query.isError);
  const pending = queries.some((query) => !query.currentData && !query.isError);
  const fetching = queries.some((query) => query.isFetching);
  const schedule = useMemo(() => {
    const range = monthBounds(month);
    try {
      return {
        lanes: slots.map((slot) => ({
          slot,
          segments: buildTrackingIconSchedule(
            (slot === 'driver'
              ? driver.currentData
              : destination.currentData) ?? [],
            slot,
            range.from,
            range.to
          ),
        })),
        error: false,
      };
    } catch {
      return { lanes: [], error: true };
    }
  }, [month, slots, driver.currentData, destination.currentData]);

  const updateParams = (changes: Record<string, string | null>) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      Object.entries(changes).forEach(([key, value]) =>
        value === null ? next.delete(key) : next.set(key, value)
      );
      return next;
    });
  };
  const changeMonth = (date: Date) =>
    updateParams({ month: monthValue(date), day: null });
  const refresh = () => {
    queries.forEach((query) => {
      void query.refetch();
    });
  };

  return (
    <section className='flex h-full w-full min-w-0 flex-col gap-6'>
      <NavigateLinks
        actualPage='Programación de íconos'
        breadcrumbs={breadcrumbs}
      />
      <div>
        <h1 className='text-xl font-medium text-gray-900'>
          Programación de íconos
        </h1>
        <p className='mt-2 text-sm text-gray-600'>
          Consultá qué ícono aparecerá en cada período. Los especiales
          reemplazan al predeterminado durante su vigencia.
        </p>
      </div>
      <div className='rounded-lg border border-gray-200 bg-white px-4 pt-4 sm:px-6'>
        <div className='flex flex-wrap items-center justify-between gap-4'>
          <div className='flex flex-wrap items-center gap-2'>
            <Tooltip title='Mes anterior'>
              <span>
                <IconButton
                  aria-label='Mes anterior'
                  disabled={
                    month.getFullYear() === 1900 && month.getMonth() === 0
                  }
                  onClick={() =>
                    changeMonth(
                      new Date(month.getFullYear(), month.getMonth() - 1, 1)
                    )
                  }
                >
                  <ArrowBackIosNew fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <TextField
              label='Mes'
              type='month'
              size='small'
              value={monthKey}
              inputProps={{ min: '1900-01', max: '9999-12' }}
              onChange={(event) => {
                const next = parseMonth(event.target.value);
                if (next) changeMonth(next);
              }}
              sx={{ width: 205 }}
            />
            <Tooltip title='Mes siguiente'>
              <span>
                <IconButton
                  aria-label='Mes siguiente'
                  disabled={
                    month.getFullYear() === 9999 && month.getMonth() === 11
                  }
                  onClick={() =>
                    changeMonth(
                      new Date(month.getFullYear(), month.getMonth() + 1, 1)
                    )
                  }
                >
                  <ArrowForwardIos fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              onClick={() =>
                updateParams({
                  month: monthValue(now),
                  day: String(now.getDate()),
                })
              }
            >
              Hoy
            </Button>
          </div>
          <div className='flex items-center gap-3'>
            <TextField
              select
              label='Mostrar'
              value={slotFilter}
              size='small'
              onChange={(event) =>
                updateParams({
                  slot:
                    event.target.value === 'all' ? null : event.target.value,
                })
              }
              sx={{ minWidth: 180 }}
            >
              <MenuItem value='all'>Repartidor y destino</MenuItem>
              <MenuItem value='driver'>Repartidor</MenuItem>
              <MenuItem value='destination'>Destino</MenuItem>
            </TextField>
            <Tooltip title='Actualizar programación'>
              <span>
                <IconButton
                  aria-label='Actualizar programación'
                  disabled={fetching || pending}
                  onClick={refresh}
                >
                  {fetching ? <CircularProgress size={22} /> : <Refresh />}
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
        <Tabs
          value={view}
          onChange={(_, value: 'timeline' | 'calendar') =>
            updateParams({ view: value })
          }
          aria-label='Vista de programación'
          sx={{ mt: 2 }}
        >
          <Tab
            value='timeline'
            label='Línea de tiempo'
            icon={<Timeline fontSize='small' />}
            iconPosition='start'
            id='schedule-tab-timeline'
            aria-controls='schedule-panel'
          />
          <Tab
            value='calendar'
            label='Calendario'
            icon={<CalendarMonth fontSize='small' />}
            iconPosition='start'
            id='schedule-tab-calendar'
            aria-controls='schedule-panel'
          />
        </Tabs>
      </div>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <h2 className='text-lg font-medium capitalize'>
          {monthTitle.format(month)}
        </h2>
        <div
          className='flex flex-wrap items-center gap-4 text-xs text-gray-600'
          aria-label='Referencias'
        >
          <span className='flex items-center gap-2'>
            <span
              aria-hidden
              className='h-3 w-3 rounded border border-emerald-300 bg-emerald-50'
            />
            Especial
          </span>
          <span className='flex items-center gap-2'>
            <span
              aria-hidden
              className='h-3 w-3 rounded border border-gray-300 bg-gray-100'
            />
            Predeterminado
          </span>
          <span className='flex items-center gap-2'>
            <span
              aria-hidden
              className='h-3 w-3 rounded border border-amber-300 bg-amber-50'
            />
            Sin predeterminado
          </span>
        </div>
      </div>
      <div
        id='schedule-panel'
        role='tabpanel'
        aria-labelledby={`schedule-tab-${view}`}
        aria-busy={pending || fetching}
      >
        {failed ? (
          <Alert
            severity='error'
            action={
              <Button color='inherit' onClick={refresh} disabled={fetching}>
                Reintentar
              </Button>
            }
          >
            No se pudo cargar la programación completa. Volvé a intentarlo.
          </Alert>
        ) : pending ? (
          <div
            role='status'
            className='flex items-center justify-center gap-3 rounded-lg bg-white p-12'
          >
            <CircularProgress size={24} />
            Cargando programación…
          </div>
        ) : schedule.error ? (
          <Alert
            severity='error'
            action={
              <Button color='inherit' onClick={refresh} disabled={fetching}>
                Reintentar
              </Button>
            }
          >
            Hay fechas inválidas o íconos habilitados que se superponen. Revisá
            los listados para mostrar la programación.
          </Alert>
        ) : (
          <TrackingIconScheduleView
            month={month}
            lanes={schedule.lanes}
            view={view}
            selectedDay={selectedDay}
            onSelectDay={(selected) =>
              updateParams({ day: String(selected.getDate()) })
            }
          />
        )}
      </div>
      <p className='pb-6 text-xs text-gray-500'>
        Se muestran los íconos habilitados según la configuración actual; no es
        un historial de cambios. Horarios en tu zona local (
        {Intl.DateTimeFormat().resolvedOptions().timeZone}). La hora de fin
        marca el inicio del siguiente período.
      </p>
    </section>
  );
};

export default TrackingIconSchedulePage;
